// src/components/LivePreview.js
import React from 'react';
import '../styles/LivePreview.css';
import { QRCodeCanvas } from 'qrcode.react';

/* ======================== Helpers ======================== */
const extractWaNumber = (url) => {
  if (!url || !url.includes('wa.me/')) return '';
  return `+${url.split('wa.me/')[1].replace(/[^0-9]/g, '')}`;
};

const buildHrefFor = (key, rawValue) => {
  if (!rawValue) return '';
  switch (key) {
    case 'phone':
      return rawValue.startsWith('tel:') ? rawValue : `tel:${rawValue}`;
    case 'email':
      return rawValue.startsWith('mailto:') ? rawValue : `mailto:${rawValue}`;
    case 'whatsapp':
      return rawValue;
    default:
      return rawValue;
  }
};

const buildDisplayTextFor = (key, rawValue) => {
  if (!rawValue) return '';
  switch (key) {
    case 'phone':
      return rawValue.replace(/^tel:/, '');
    case 'email':
      return rawValue.replace(/^mailto:/, '');
    case 'whatsapp':
      return extractWaNumber(rawValue) || rawValue;
    default:
      return rawValue;
  }
};

// simple emoji icons (يمكنك استبدالها لاحقًا بـ SVG)
const getIcon = (key) => {
  const map = {
    phone: '📞',
    email: '✉️',
    whatsapp: '💬',
    instagram: '📸',
    facebook: '📘',
    tiktok: '🎵',
    linkedin: '💼',
    snapchat: '👻',
    x: '𝕏',
  };
  return map[key] || '🔗';
};

// Hex + opacity → rgba()
const hexToRgba = (hex, alpha = 1) => {
  try {
    const h = String(hex || '#000').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
  } catch {
    return `rgba(0,0,0,${Math.min(Math.max(alpha, 0), 1)})`;
  }
};

/* ======================== Component ======================== */
function LivePreview({ profile }) {
  if (!profile) {
    return (
      <div className="live-preview-shell">
        <div className="lp-empty">Loading preview...</div>
      </div>
    );
  }

  const {
    photoURL,
    displayName,
    username,
    theme = 'light',
    themeSettings = {},
    contactLinks = {},
    contactLinksDisplay = 'icons',
    customLinks = [],
    // NEW: يُرسل من UserDashboard بعد القصّ
    photoHasAlpha = false,
  } = profile;

  /* ========== Theme wrappers (الخلفية والألوان العامة) ========== */
  const getWrapperStyle = () => {
    // NEW: global override regardless of theme
    const {
      forceBg = {
        mode: 'inherit', // 'inherit' | 'color' | 'gradient'
        color: '#ffffff',
        gradientStart: '#4a6f7b',
        gradientEnd: '#2f4e61',
        gradientAngle: 180,
        fontFamily: 'Arial, sans-serif',
      },
    } = themeSettings;

    // If user forces background, apply it for all themes
    if (forceBg && forceBg.mode && forceBg.mode !== 'inherit') {
      const styleObj = { fontFamily: forceBg.fontFamily || 'Arial, sans-serif' };
      if (forceBg.mode === 'color') {
        styleObj.background = forceBg.color || '#ffffff';
        styleObj.color = '#000';
      } else if (forceBg.mode === 'gradient') {
        const ang = forceBg.gradientAngle ?? 180;
        styleObj.background = `linear-gradient(${ang}deg, ${forceBg.gradientStart || '#4a6f7b'}, ${forceBg.gradientEnd || '#2f4e61'})`;
        styleObj.color = '#000';
      }
      return styleObj;
    }

    if (theme === 'custom') {
      const {
        backgroundType = 'color',
        backgroundColor = '#4a6f7b',
        gradientStart = '#4a6f7b',
        gradientEnd = '#2f4e61',
        gradientAngle = 180,
        fontFamily = 'Arial, sans-serif',
      } = themeSettings;

      const styleObj = { fontFamily, color: '#000' };
      if (backgroundType === 'gradient') {
        styleObj.background = `linear-gradient(${gradientAngle}deg, ${gradientStart}, ${gradientEnd})`;
      } else {
        styleObj.background = backgroundColor;
      }
      return styleObj;
    }

    // Presets
    switch (theme) {
      case 'dark':
        return { background: '#111', color: '#fff', fontFamily: 'Arial, sans-serif' };
      case 'forest':
        return { background: 'linear-gradient(160deg,#0f3d2e,#0a1f1a)', color: '#e9ffe9' };
      case 'neon':
        return { background: 'radial-gradient(circle at 20% 20%, #1a002b 0%, #000 80%)', color: '#39ff14' };
      case 'sunset':
        return { background: 'linear-gradient(180deg, #ff9a3c 0%, #ff2e63 50%, #120078 100%)', color: '#fff' };
      case 'light':
      default:
        return { background: 'linear-gradient(180deg, #4a6f7b 0%, #31506a 60%, #ffffff 100%)', color: '#000' };
    }
  };

  const getButtonStyleVars = () => {
    if (theme === 'custom') {
      const {
        buttonColor = '#ffffff',
        buttonFontColor = '#000000',
        buttonStyle = 'rounded',
      } = themeSettings;

      const radius = buttonStyle === 'square' ? '12px' : '20px';
      return {
        backgroundColor: buttonStyle === 'outline' ? '#ffffff' : buttonColor,
        color: buttonFontColor,
        border: buttonStyle === 'outline' ? `2px solid ${buttonColor}` : '2px solid transparent',
        borderRadius: radius,
        boxShadow: '0 18px 32px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)',
      };
    }
    return {
      backgroundColor: '#ffffff',
      color: '#1a1a1a',
      border: '2px solid transparent',
      borderRadius: '20px',
      boxShadow: '0 18px 32px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)',
    };
  };

  /* ========== Avatar controls (الخيارات المتقدمة للصورة) ========== */
  const {
    avatarSize = 120,
    avatarShape = 'circle', // circle | rounded | square | hexagon | diamond | blob
    avatarObjectFit = 'cover',
    avatarBorderWidth = 4,
    avatarBorderColor = 'rgba(255,255,255,0.9)',
    avatarBorderStyle = 'solid', // solid | dashed | gradient
    avatarBorderGradientStart = '#7F7FD5',
    avatarBorderGradientEnd = '#86A8E7',

    avatarShadowPreset = 'soft', // none | soft | deep | glow
    avatarRotation = 0,
    avatarSkew = 0,

    avatarFilters = {},
    avatarBackplate = {},
    avatarRing = {},
    avatarBadge = {},
    avatarHover = {},
    avatarAlign = 'center', // center | left

    // خلفية اختيارية خلف الصورة (لا تفرغ الخلفية داخلها)
    avatarBgMode = 'transparent', // 'transparent' | 'color'
    avatarBgColor = '#f4f4f4',
    avatarBgOpacity = 1,
    // NEW: متى نطبّق الخلفية
    avatarBgApplyMode = 'onlyTransparent', // 'onlyTransparent' | 'always'
  } = themeSettings;

  const shadowMap = {
    none: 'none',
    soft: '0 10px 20px rgba(0,0,0,.22)',
    deep: '0 24px 48px rgba(0,0,0,.35)',
    glow: '0 0 0 2px rgba(255,255,255,.35), 0 0 32px rgba(0,195,255,.55)',
  };

  // clip-path للأشكال الخاصة
  const shapeClipPath =
    avatarShape === 'hexagon'
      ? 'polygon(25% 6.7%, 75% 6.7%, 100% 50%, 75% 93.3%, 25% 93.3%, 0% 50%)'
      : avatarShape === 'diamond'
      ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
      : null;

  const borderRadius =
    avatarShape === 'circle'
      ? '999px'
      : avatarShape === 'rounded'
      ? '22px'
      : avatarShape === 'blob'
      ? '35% 65% 70% 30% / 30% 30% 70% 70%'
      : '0px';

  // فلاتر CSS
  const f = {
    grayscale: avatarFilters.grayscale ?? 0,
    sepia: avatarFilters.sepia ?? 0,
    blur: avatarFilters.blur ?? 0,
    brightness: avatarFilters.brightness ?? 100,
    contrast: avatarFilters.contrast ?? 100,
    saturate: avatarFilters.saturate ?? 100,
  };
  const filterCss =
    `grayscale(${f.grayscale}%) sepia(${f.sepia}%) blur(${f.blur}px) ` +
    `brightness(${f.brightness}%) contrast(${f.contrast}%) saturate(${f.saturate}%)`;

  // Ring
  const ringEnabled = !!avatarRing.enabled;
  const ringThickness = avatarRing.thickness ?? 4;
  const ringColor = avatarRing.color || '#00D4FF';

  // Backplate
  const bpEnabled = !!avatarBackplate.enabled;
  const bpColor = avatarBackplate.color || 'rgba(255,255,255,0.35)';
  const bpPad = avatarBackplate.padding ?? 8;
  const bpRadius = avatarBackplate.radius ?? 18;

  const isCenter = avatarAlign === 'center';

  // إجمالي مساحة الحاوية (مع الـ ring و backplate)
  const containerSize = avatarSize + (bpEnabled ? bpPad * 2 : 0) + (ringEnabled ? ringThickness * 2 : 0);

  const avatarWrapStyle = {
    width: containerSize,
    height: containerSize,
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    transform: `rotate(${avatarRotation}deg) skew(${avatarSkew}deg)`,
    transition: 'transform .25s ease',
    ...(isCenter ? { margin: '0 auto' } : {}),
  };

  // طبقة Backplate (خلفية حول الصورة كاملة)
  const innerPlateStyle = bpEnabled
    ? {
        background: bpColor,
        padding: bpPad,
        borderRadius: avatarShape === 'circle' ? '999px' : bpRadius,
        display: 'grid',
        placeItems: 'center',
        width: '100%',
        height: '100%',
      }
    : null;

  // حلقة محيطة (box-shadow كـ حلقة)
  if (ringEnabled) {
    avatarWrapStyle.boxShadow = `0 0 0 ${ringThickness}px ${ringColor}`;
    avatarWrapStyle.borderRadius = avatarShape === 'circle' ? '999px' : borderRadius;
  }

  // غلاف حد متدرّج
  const gradientBorderWrap =
    avatarBorderStyle === 'gradient'
      ? {
          padding: avatarBorderWidth,
          borderRadius: avatarShape === 'circle' ? '999px' : borderRadius,
          background: `linear-gradient(135deg, ${avatarBorderGradientStart}, ${avatarBorderGradientEnd})`,
          display: 'grid',
          placeItems: 'center',
        }
      : null;

  // NEW: متى نطبّق الخلفية خلف الصورة؟
  const shouldApplyAvatarBg =
    avatarBgMode === 'color' &&
    (avatarBgApplyMode === 'always' || photoHasAlpha === true);

  // حاوية الصورة نفسها مع خلفية اختيارية (لا تفريغ داخل الصورة)
  const imgContainerStyle = {
    width: avatarSize,
    height: avatarSize,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    ...(shapeClipPath ? { clipPath: shapeClipPath } : { borderRadius }),
    background: shouldApplyAvatarBg
      ? hexToRgba(
          avatarBgColor,
          typeof avatarBgOpacity === 'number' ? avatarBgOpacity : 1
        )
      : 'transparent',
  };

  // نمط الصورة نفسها
  const baseImgStyle = {
    width: '100%',
    height: '100%',
    objectFit: avatarObjectFit,
    filter: filterCss,
    boxShadow: shadowMap[avatarShadowPreset] || shadowMap.soft,
    transition: 'transform .25s ease, filter .25s ease, box-shadow .25s ease',
    // إذا لم نستخدم clipPath على الحاوية، نستخدم borderRadius/clip هنا
    ...(shapeClipPath ? {} : { borderRadius }),
    ...(avatarBorderStyle !== 'gradient'
      ? { border: `${avatarBorderWidth}px ${avatarBorderStyle === 'dashed' ? 'dashed' : 'solid'} ${avatarBorderColor}` }
      : {}),
  };

  // Hover effects
  const hoverScale = avatarHover.scale ? 1.04 : 1;
  const onImgMouseEnter = (e) => {
    e.currentTarget.style.transform = `scale(${hoverScale})${avatarHover.tilt ? ' perspective(600px) rotateX(4deg) rotateY(-4deg)' : ''}`;
  };
  const onImgMouseLeave = (e) => {
    e.currentTarget.style.transform = 'none';
  };

  // Badge
  const badge = avatarBadge?.enabled
    ? {
        text: avatarBadge.text || '✓',
        bg: avatarBadge.bg || '#00C853',
        color: avatarBadge.color || '#fff',
        size: avatarBadge.size ?? 18,
        pos: avatarBadge.position || 'br',
      }
    : null;

  const badgeStyle = badge
    ? (() => {
        const posMap = {
          br: { bottom: -4, right: -4 },
          tr: { top: -4, right: -4 },
          bl: { bottom: -4, left: -4 },
          tl: { top: -4, left: -4 },
        };
        const p = posMap[badge.pos] || posMap.br;
        return {
          position: 'absolute',
          ...p,
          width: badge.size + 10,
          height: badge.size + 10,
          borderRadius: '999px',
          background: badge.bg,
          color: badge.color,
          display: 'grid',
          placeItems: 'center',
          fontSize: badge.size * 0.8,
          boxShadow: '0 6px 12px rgba(0,0,0,.25)',
          border: '2px solid rgba(255,255,255,.9)',
        };
      })()
    : null;

  /* ========== روابط التواصل النشطة ========== */
  const activeLinks = [
    { key: 'phone', label: 'Phone', value: contactLinks.phone },
    { key: 'email', label: 'Email', value: contactLinks.email },
    { key: 'whatsapp', label: 'WhatsApp', value: contactLinks.whatsapp },
    { key: 'instagram', label: 'Instagram', value: contactLinks.instagram },
    { key: 'facebook', label: 'Facebook', value: contactLinks.facebook },
    { key: 'tiktok', label: 'TikTok', value: contactLinks.tiktok },
    { key: 'linkedin', label: 'LinkedIn', value: contactLinks.linkedin },
    { key: 'snapchat', label: 'Snapchat', value: contactLinks.snapchat },
    { key: 'x', label: 'X', value: contactLinks.x },
  ].filter((item) => item.value && String(item.value).trim() !== '');

  /* ========== Contact block renderers ========== */
  const renderContactBlock = () => {
    if (activeLinks.length === 0) return null;
    const btnVars = getButtonStyleVars();

    // 1) icons
    if (contactLinksDisplay === 'icons') {
      return (
        <div className="lp-contact-icons-row">
          {activeLinks.map((item) => (
            <a
              key={item.key}
              className="lp-contact-icon"
              href={buildHrefFor(item.key, item.value)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getIcon(item.key)}
            </a>
          ))}
        </div>
      );
    }

    // 2) buttons
    if (contactLinksDisplay === 'buttons') {
      return (
        <div className="lp-contact-buttons-col">
          {activeLinks.map((item) => (
            <a
              key={item.key}
              className="lp-big-link-card"
              href={buildHrefFor(item.key, item.value)}
              target="_blank"
              rel="noopener noreferrer"
              style={btnVars}
            >
              <span className="lp-big-link-inner">
                <span className="lp-big-emoji">{getIcon(item.key)}</span>
                <span className="lp-big-label">{item.label}</span>
              </span>
            </a>
          ))}
        </div>
      );
    }

    // 3) chips
    if (contactLinksDisplay === 'chips') {
      return (
        <div className="lp-contact-chips-row">
          {activeLinks.map((item) => (
            <a
              key={item.key}
              className="lp-chip-pill"
              href={buildHrefFor(item.key, item.value)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="lp-chip-emoji">{getIcon(item.key)}</span>
              <span className="lp-chip-text">{item.label}</span>
            </a>
          ))}
        </div>
      );
    }

    // 4) list
    if (contactLinksDisplay === 'list') {
      return (
        <div className="lp-contact-list-col">
          {activeLinks.map((item, idx) => (
            <div
              key={item.key}
              className="lp-list-row"
              style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.15)' }}
            >
              <div className="lp-list-left">
                <span className="lp-list-emoji">{getIcon(item.key)}</span>
                <span className="lp-list-label">{item.label}</span>
              </div>
              <a
                className="lp-list-value"
                href={buildHrefFor(item.key, item.value)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {buildDisplayTextFor(item.key, item.value)}
              </a>
            </div>
          ))}
        </div>
      );
    }

    // 5) grid
    if (contactLinksDisplay === 'grid') {
      return (
        <div className="lp-contact-grid">
          {activeLinks.map((item) => (
            <a
              key={item.key}
              className="lp-grid-card"
              href={buildHrefFor(item.key, item.value)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="lp-grid-emoji">{getIcon(item.key)}</div>
              <div className="lp-grid-text">{item.label}</div>
            </a>
          ))}
        </div>
      );
    }

    // 6) minimal_text
    if (contactLinksDisplay === 'minimal_text') {
      return (
        <div className="lp-contact-minimal">
          {activeLinks.map((item) => (
            <a
              key={item.key}
              className="lp-min-row"
              href={buildHrefFor(item.key, item.value)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="lp-min-emoji">{getIcon(item.key)}</span>
              <span className="lp-min-label">{item.label}</span>
              <span className="lp-min-value">
                {buildDisplayTextFor(item.key, item.value)}
              </span>
            </a>
          ))}
        </div>
      );
    }

    // 7) qr_block
    if (contactLinksDisplay === 'qr_block') {
      const featured = activeLinks.slice(0, 3);
      return (
        <div className="lp-qr-block-wrapper">
          <div className="lp-contact-buttons-col">
            {featured.map((item) => (
              <a
                key={item.key}
                className="lp-big-link-card"
                href={buildHrefFor(item.key, item.value)}
                target="_blank"
                rel="noopener noreferrer"
                style={getButtonStyleVars()}
              >
                <span className="lp-big-link-inner">
                  <span className="lp-big-emoji">{getIcon(item.key)}</span>
                  <span className="lp-big-label">{item.label}</span>
                </span>
              </a>
            ))}
          </div>

          {username && (
            <div className="lp-qr-box">
              <QRCodeCanvas
                value={`${window.location.origin}/${username}`}
                size={120}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
              />
              <div className="lp-qr-username">@{username}</div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  /* ========== Custom links (الأزرار المخصصة) ========== */
  const renderCustomLinks = () => {
    if (!customLinks || customLinks.length === 0) return null;
    const btnVars = getButtonStyleVars();
    return (
      <div className="lp-mainlinks-col">
        {customLinks.map((link) => (
          <a
            key={link.id}
            className="lp-mainlink-btn"
            href={link.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            style={btnVars}
          >
            {link.title || 'Untitled Link'}
          </a>
        ))}
      </div>
    );
  };

  /* ========== Final render ========== */
  const wrapperStyle = getWrapperStyle();
  const textColor = wrapperStyle?.color || '#000';

  return (
    <div className="live-preview-shell" style={wrapperStyle}>
      <div className="lp-card" style={{ color: textColor }}>
        {/* TOP: avatar + name */}
        <div
          className="lp-top"
          style={
            !photoURL || themeSettings.avatarAlign === 'center'
              ? { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10 }
              : { display: 'grid', gridTemplateColumns: `${themeSettings.avatarSize + ((themeSettings.avatarBackplate?.enabled ? (themeSettings.avatarBackplate.padding ?? 8) * 2 : 0)) + ((themeSettings.avatarRing?.enabled ? (themeSettings.avatarRing.thickness ?? 4) * 2 : 0))}px 1fr`, gap: 14, alignItems: 'center' }
          }
        >
          {photoURL && (
            <div style={(() => {
              // إعادة بناء بعض متغيرات الغلاف هنا للتوافق مع التعبير أعلاه
              const ringEnabledLocal = !!(themeSettings.avatarRing?.enabled);
              const ringThicknessLocal = themeSettings.avatarRing?.thickness ?? 4;
              const ringColorLocal = themeSettings.avatarRing?.color || '#00D4FF';
              const bpEnabledLocal = !!(themeSettings.avatarBackplate?.enabled);

              const base = {
                width: themeSettings.avatarSize ?? 120 + (bpEnabledLocal ? (themeSettings.avatarBackplate?.padding ?? 8) * 2 : 0) + (ringEnabledLocal ? ringThicknessLocal * 2 : 0),
                height: themeSettings.avatarSize ?? 120 + (bpEnabledLocal ? (themeSettings.avatarBackplate?.padding ?? 8) * 2 : 0) + (ringEnabledLocal ? ringThicknessLocal * 2 : 0),
                display: 'grid',
                placeItems: 'center',
                position: 'relative',
                transform: `rotate(${themeSettings.avatarRotation ?? 0}deg) skew(${themeSettings.avatarSkew ?? 0}deg)`,
                transition: 'transform .25s ease',
                ...(themeSettings.avatarAlign === 'center' ? { margin: '0 auto' } : {}),
              };
              if (ringEnabledLocal) {
                base.boxShadow = `0 0 0 ${ringThicknessLocal}px ${ringColorLocal}`;
                base.borderRadius = (themeSettings.avatarShape || 'circle') === 'circle' ? '999px' :
                  (themeSettings.avatarShape === 'rounded' ? '22px' :
                    (themeSettings.avatarShape === 'blob' ? '35% 65% 70% 30% / 30% 30% 70% 70%' : '0px'));
              }
              return base;
            })()}>
              {/* Backplate */}
              {themeSettings.avatarBackplate?.enabled ? (
                <div style={{
                  background: themeSettings.avatarBackplate.color || 'rgba(255,255,255,0.35)',
                  padding: themeSettings.avatarBackplate.padding ?? 8,
                  borderRadius: (themeSettings.avatarShape || 'circle') === 'circle' ? '999px' : (themeSettings.avatarBackplate.radius ?? 18),
                  display: 'grid',
                  placeItems: 'center',
                  width: '100%',
                  height: '100%',
                }}>
                  {/* Gradient border wrap */}
                  {themeSettings.avatarBorderStyle === 'gradient' ? (
                    <div style={{
                      padding: themeSettings.avatarBorderWidth ?? 4,
                      borderRadius: (themeSettings.avatarShape || 'circle') === 'circle' ? '999px' :
                        (themeSettings.avatarShape === 'rounded' ? '22px' :
                          (themeSettings.avatarShape === 'blob' ? '35% 65% 70% 30% / 30% 30% 70% 70%' : '0px')),
                      background: `linear-gradient(135deg, ${themeSettings.avatarBorderGradientStart || '#7F7FD5'}, ${themeSettings.avatarBorderGradientEnd || '#86A8E7'})`,
                      display: 'grid',
                      placeItems: 'center',
                    }}>
                      <div style={imgContainerStyle}>
                        <img
                          src={photoURL}
                          alt="profile"
                          style={baseImgStyle}
                          onMouseEnter={onImgMouseEnter}
                          onMouseLeave={onImgMouseLeave}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={imgContainerStyle}>
                      <img
                        src={photoURL}
                        alt="profile"
                        style={baseImgStyle}
                        onMouseEnter={onImgMouseEnter}
                        onMouseLeave={onImgMouseLeave}
                      />
                    </div>
                  )}
                </div>
              ) : (
                // بدون Backplate
                <>
                  {themeSettings.avatarBorderStyle === 'gradient' ? (
                    <div style={{
                      padding: themeSettings.avatarBorderWidth ?? 4,
                      borderRadius: (themeSettings.avatarShape || 'circle') === 'circle' ? '999px' :
                        (themeSettings.avatarShape === 'rounded' ? '22px' :
                          (themeSettings.avatarShape === 'blob' ? '35% 65% 70% 30% / 30% 30% 70% 70%' : '0px')),
                      background: `linear-gradient(135deg, ${themeSettings.avatarBorderGradientStart || '#7F7FD5'}, ${themeSettings.avatarBorderGradientEnd || '#86A8E7'})`,
                      display: 'grid',
                      placeItems: 'center',
                    }}>
                      <div style={imgContainerStyle}>
                        <img
                          src={photoURL}
                          alt="profile"
                          style={baseImgStyle}
                          onMouseEnter={onImgMouseEnter}
                          onMouseLeave={onImgMouseLeave}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={imgContainerStyle}>
                      <img
                        src={photoURL}
                        alt="profile"
                        style={baseImgStyle}
                        onMouseEnter={onImgMouseEnter}
                        onMouseLeave={onImgMouseLeave}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Badge */}
              {(() => {
                if (!themeSettings.avatarBadge?.enabled) return null;
                const badge = {
                  text: themeSettings.avatarBadge.text || '✓',
                  bg: themeSettings.avatarBadge.bg || '#00C853',
                  color: themeSettings.avatarBadge.color || '#fff',
                  size: themeSettings.avatarBadge.size ?? 18,
                  pos: themeSettings.avatarBadge.position || 'br',
                };
                const posMap = {
                  br: { bottom: -4, right: -4 },
                  tr: { top: -4, right: -4 },
                  bl: { bottom: -4, left: -4 },
                  tl: { top: -4, left: -4 },
                };
                const p = posMap[badge.pos] || posMap.br;
                return (
                  <div
                    style={{
                      position: 'absolute',
                      ...p,
                      width: badge.size + 10,
                      height: badge.size + 10,
                      borderRadius: '999px',
                      background: badge.bg,
                      color: badge.color,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: badge.size * 0.8,
                      boxShadow: '0 6px 12px rgba(0,0,0,.25)',
                      border: '2px solid rgba(255,255,255,.9)',
                    }}
                  >
                    {badge.text}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="lp-name-block" style={{ alignSelf: 'center' }}>
            {displayName && (
              <div className="lp-displayName" style={{ lineHeight: 1.15, fontWeight: 800, fontSize: 22 }}>
                {displayName}
              </div>
            )}
            {username && (
              <div className="lp-username" style={{ opacity: 0.85, fontSize: 14, fontWeight: 600 }}>
                @{username}
              </div>
            )}
          </div>
        </div>

        {renderContactBlock()}
        {renderCustomLinks()}
      </div>
    </div>
  );
}

export default LivePreview;
